import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Plus, BookOpen, Layers, Target, GraduationCap,
  Pencil, Trash2, Search, ListTree, Upload, Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  listCurricula, listClasses, listSubjects, listTopics, listSubtopics, listOutcomes,
  upsertCurriculum, deleteCurriculum,
  upsertClass, deleteClass,
  upsertSubject, deleteSubject,
  upsertTopic, deleteTopic,
  upsertSubtopic, deleteSubtopic,
  upsertOutcome, deleteOutcome,
  bulkImportCurriculum,
} from "@/lib/curriculum.functions";


export const Route = createFileRoute("/admin/curriculum")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "Curriculum · MAKAI" }] }),
  component: Page,
});

const LEVELS = ["nursery", "primary", "o_level", "a_level", "tvet", "university"] as const;
const BLOOM = ["remember", "understand", "apply", "analyze", "evaluate", "create"] as const;

type Field =
  | { name: string; label: string; required?: boolean; textarea?: boolean; type?: "text" | "number" }
  | { name: string; label: string; required?: boolean; select: readonly string[] };

type AnyRow = Record<string, unknown> & { id?: string };

function Page() {
  const qc = useQueryClient();
  const fnListCur = useServerFn(listCurricula);
  const fnListCls = useServerFn(listClasses);
  const fnListSub = useServerFn(listSubjects);
  const fnListTop = useServerFn(listTopics);
  const fnListSt = useServerFn(listSubtopics);
  const fnListOut = useServerFn(listOutcomes);

  const curricula = useQuery({ queryKey: ["cur"], queryFn: () => fnListCur() });
  const [curId, setCurId] = useState<string>("");
  const [classId, setClassId] = useState<string>("");
  const [subjectId, setSubjectId] = useState<string>("");
  const [topicId, setTopicId] = useState<string>("");
  const [subtopicId, setSubtopicId] = useState<string>("");

  const cur = curId || curricula.data?.[0]?.id || "";
  const activeCur = curricula.data?.find((c) => c.id === cur);

  const classes = useQuery({
    queryKey: ["cur-classes", cur],
    queryFn: () => fnListCls({ data: { curriculumId: cur } }),
    enabled: !!cur,
  });
  const subjects = useQuery({
    queryKey: ["cur-subjects", cur],
    queryFn: () => fnListSub({ data: { curriculumId: cur } }),
    enabled: !!cur,
  });
  const topics = useQuery({
    queryKey: ["cur-topics", subjectId, classId],
    queryFn: () => fnListTop({ data: { subjectId, classId } }),
    enabled: !!subjectId && !!classId,
  });
  const subtopics = useQuery({
    queryKey: ["cur-subs", topicId],
    queryFn: () => fnListSt({ data: { topicId } }),
    enabled: !!topicId,
  });
  const outcomes = useQuery({
    queryKey: ["cur-out", subtopicId, topicId],
    queryFn: () =>
      fnListOut({ data: subtopicId ? { subtopicId } : { topicId } }),
    enabled: !!topicId,
  });

  // Mutations
  const saveCur = useServerFn(upsertCurriculum);
  const delCur = useServerFn(deleteCurriculum);
  const saveCls = useServerFn(upsertClass);
  const delCls = useServerFn(deleteClass);
  const saveSub = useServerFn(upsertSubject);
  const delSub = useServerFn(deleteSubject);
  const saveTop = useServerFn(upsertTopic);
  const delTop = useServerFn(deleteTopic);
  const saveSt = useServerFn(upsertSubtopic);
  const delSt = useServerFn(deleteSubtopic);
  const saveOut = useServerFn(upsertOutcome);
  const delOut = useServerFn(deleteOutcome);

  function refetch(kind: "cur" | "class" | "subj" | "top" | "sub" | "out") {
    if (kind === "cur") qc.invalidateQueries({ queryKey: ["cur"] });
    if (kind === "class") qc.invalidateQueries({ queryKey: ["cur-classes"] });
    if (kind === "subj") qc.invalidateQueries({ queryKey: ["cur-subjects"] });
    if (kind === "top") qc.invalidateQueries({ queryKey: ["cur-topics"] });
    if (kind === "sub") qc.invalidateQueries({ queryKey: ["cur-subs"] });
    if (kind === "out") qc.invalidateQueries({ queryKey: ["cur-out"] });
  }

  // Search across loaded data
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  function filt<T extends { name?: string; statement?: string; code?: string | null }>(arr: T[] | undefined) {
    if (!arr) return [];
    if (!q) return arr;
    return arr.filter((r) =>
      [r.name, r.statement, r.code].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }

  const tree = useMemo(() => ({
    classes: filt(classes.data),
    subjects: filt(subjects.data),
    topics: filt(topics.data),
    subtopics: filt(subtopics.data),
    outcomes: filt(outcomes.data),
  }), [classes.data, subjects.data, topics.data, subtopics.data, outcomes.data, q]);

  return (
    <AppShell role="admin" title="Curriculum" subtitle="Manage Uganda NCDC content taxonomy">
      <div className="space-y-6 p-4 md:p-6">
        {/* Curriculum picker + search */}
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5" /> Curriculum</CardTitle>
            <div className="flex items-center gap-2">
              <BulkImportDialog onDone={() => {
                qc.invalidateQueries({ queryKey: ["cur"] });
                qc.invalidateQueries({ queryKey: ["cur-classes"] });
                qc.invalidateQueries({ queryKey: ["cur-subjects"] });
                qc.invalidateQueries({ queryKey: ["cur-topics"] });
                qc.invalidateQueries({ queryKey: ["cur-subs"] });
                qc.invalidateQueries({ queryKey: ["cur-out"] });
              }} />

              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />New</Button>}
                title="New curriculum"
                fields={[
                  { name: "code", label: "Code", required: true },
                  { name: "name", label: "Name", required: true },
                  { name: "country", label: "Country" },
                  { name: "authority", label: "Authority" },
                ]}
                onSubmit={async (v) => { await saveCur({ data: v as never }); toast.success("Curriculum saved"); refetch("cur"); }}
              />
              {activeCur && (
                <>
                  <EditDialog
                    trigger={<Button size="sm" variant="ghost"><Pencil className="h-4 w-4" /></Button>}
                    title="Edit curriculum"
                    initial={activeCur as AnyRow}
                    fields={[
                      { name: "code", label: "Code", required: true },
                      { name: "name", label: "Name", required: true },
                      { name: "country", label: "Country" },
                      { name: "authority", label: "Authority" },
                    ]}
                    onSubmit={async (v) => { await saveCur({ data: { id: activeCur.id, ...v } as never }); toast.success("Updated"); refetch("cur"); }}
                  />
                  <DeleteButton
                    label={activeCur.name}
                    onConfirm={async () => { await delCur({ data: { id: activeCur.id } }); toast.success("Deleted"); setCurId(""); refetch("cur"); }}
                  />
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Curriculum</Label>
              <Select value={cur} onValueChange={(v) => { setCurId(v); setClassId(""); setSubjectId(""); setTopicId(""); setSubtopicId(""); }}>
                <SelectTrigger><SelectValue placeholder="Select curriculum" /></SelectTrigger>
                <SelectContent>
                  {curricula.data?.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={classId} onValueChange={(v) => { setClassId(v); setTopicId(""); setSubtopicId(""); }}>
                <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  {classes.data?.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Select value={subjectId} onValueChange={(v) => { setSubjectId(v); setTopicId(""); setSubtopicId(""); }}>
                <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>
                  {subjects.data?.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search (within loaded lists)</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter names, codes, outcomes…" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Classes + Subjects */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Column
            icon={<Layers className="h-4 w-4" />}
            title="Classes"
            disabled={!cur}
            empty={cur ? "No classes — add one." : "Pick a curriculum first."}
            items={tree.classes.map((c) => ({ id: c.id, label: c.name, code: c.code ?? "", raw: c as AnyRow }))}
            activeId={classId}
            onPick={(id) => { setClassId(id); setTopicId(""); setSubtopicId(""); }}
            addDialog={
              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add</Button>}
                title="Add class"
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code", required: true },
                  { name: "level", label: "Level", required: true, select: LEVELS },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveCls({ data: { ...v, curriculum_id: cur } as never }); toast.success("Class added"); refetch("class"); }}
              />
            }
            editDialog={(row) => (
              <EditDialog
                trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>}
                title="Edit class"
                initial={row}
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code", required: true },
                  { name: "level", label: "Level", required: true, select: LEVELS },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveCls({ data: { id: row.id, curriculum_id: cur, ...v } as never }); toast.success("Updated"); refetch("class"); }}
              />
            )}
            onDelete={async (row) => { await delCls({ data: { id: row.id! } }); toast.success("Deleted"); refetch("class"); }}
          />
          <Column
            icon={<BookOpen className="h-4 w-4" />}
            title="Subjects"
            disabled={!cur}
            empty={cur ? "No subjects — add one." : "Pick a curriculum first."}
            items={tree.subjects.map((s) => ({ id: s.id, label: s.name, code: s.code ?? "", raw: s as AnyRow }))}
            activeId={subjectId}
            onPick={(id) => { setSubjectId(id); setTopicId(""); setSubtopicId(""); }}
            addDialog={
              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add</Button>}
                title="Add subject"
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code", required: true },
                  { name: "description", label: "Description", textarea: true },
                  { name: "icon", label: "Icon" },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveSub({ data: { ...v, curriculum_id: cur, is_global: true } as never }); toast.success("Subject added"); refetch("subj"); }}
              />
            }
            editDialog={(row) => (
              <EditDialog
                trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>}
                title="Edit subject"
                initial={row}
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code", required: true },
                  { name: "description", label: "Description", textarea: true },
                  { name: "icon", label: "Icon" },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveSub({ data: { id: row.id, curriculum_id: cur, ...v } as never }); toast.success("Updated"); refetch("subj"); }}
              />
            )}
            onDelete={async (row) => { await delSub({ data: { id: row.id! } }); toast.success("Deleted"); refetch("subj"); }}
          />
        </div>

        {/* Topics, Sub-topics, Outcomes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Column
            icon={<BookOpen className="h-4 w-4" />}
            title="Topics"
            disabled={!classId || !subjectId}
            empty={classId && subjectId ? "No topics yet." : "Pick class & subject."}
            items={tree.topics.map((t) => ({ id: t.id, label: t.name, code: t.code ?? "", raw: t as AnyRow }))}
            activeId={topicId}
            onPick={(id) => { setTopicId(id); setSubtopicId(""); }}
            addDialog={
              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add</Button>}
                title="Add topic"
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code" },
                  { name: "description", label: "Description", textarea: true },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveTop({ data: { ...v, subject_id: subjectId, class_id: classId, is_global: true } as never }); toast.success("Topic added"); refetch("top"); }}
              />
            }
            editDialog={(row) => (
              <EditDialog
                trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>}
                title="Edit topic"
                initial={row}
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code" },
                  { name: "description", label: "Description", textarea: true },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveTop({ data: { id: row.id, subject_id: subjectId, class_id: classId, ...v } as never }); toast.success("Updated"); refetch("top"); }}
              />
            )}
            onDelete={async (row) => { await delTop({ data: { id: row.id! } }); toast.success("Deleted"); refetch("top"); }}
          />
          <Column
            icon={<Layers className="h-4 w-4" />}
            title="Sub-topics"
            disabled={!topicId}
            empty={topicId ? "No sub-topics." : "Pick a topic first."}
            items={tree.subtopics.map((t) => ({ id: t.id, label: t.name, code: t.code ?? "", raw: t as AnyRow }))}
            activeId={subtopicId}
            onPick={setSubtopicId}
            addDialog={
              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add</Button>}
                title="Add sub-topic"
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code" },
                  { name: "description", label: "Description", textarea: true },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveSt({ data: { ...v, topic_id: topicId } as never }); toast.success("Sub-topic added"); refetch("sub"); }}
              />
            }
            editDialog={(row) => (
              <EditDialog
                trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>}
                title="Edit sub-topic"
                initial={row}
                fields={[
                  { name: "name", label: "Name", required: true },
                  { name: "code", label: "Code" },
                  { name: "description", label: "Description", textarea: true },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveSt({ data: { id: row.id, topic_id: topicId, ...v } as never }); toast.success("Updated"); refetch("sub"); }}
              />
            )}
            onDelete={async (row) => { await delSt({ data: { id: row.id! } }); toast.success("Deleted"); refetch("sub"); }}
          />
          <Column
            icon={<Target className="h-4 w-4" />}
            title="Learning outcomes"
            disabled={!topicId}
            empty={topicId ? "No outcomes." : "Pick a topic first."}
            items={tree.outcomes.map((o) => ({
              id: o.id,
              label: o.statement,
              code: o.bloom_level ?? "",
              raw: o as AnyRow,
            }))}
            activeId=""
            onPick={() => undefined}
            addDialog={
              <EditDialog
                trigger={<Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1" />Add</Button>}
                title="Add outcome"
                fields={[
                  { name: "statement", label: "Outcome statement", required: true, textarea: true },
                  { name: "code", label: "Code" },
                  { name: "bloom_level", label: "Bloom level", select: BLOOM },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => {
                  await saveOut({ data: {
                    ...v,
                    topic_id: subtopicId ? undefined : topicId,
                    subtopic_id: subtopicId || undefined,
                  } as never });
                  toast.success("Outcome added"); refetch("out");
                }}
              />
            }
            editDialog={(row) => (
              <EditDialog
                trigger={<Button size="icon" variant="ghost" className="h-7 w-7"><Pencil className="h-3.5 w-3.5" /></Button>}
                title="Edit outcome"
                initial={row}
                fields={[
                  { name: "statement", label: "Outcome statement", required: true, textarea: true },
                  { name: "code", label: "Code" },
                  { name: "bloom_level", label: "Bloom level", select: BLOOM },
                  { name: "sort_order", label: "Sort order", type: "number" },
                ]}
                onSubmit={async (v) => { await saveOut({ data: { id: row.id, ...v } as never }); toast.success("Updated"); refetch("out"); }}
              />
            )}
            onDelete={async (row) => { await delOut({ data: { id: row.id! } }); toast.success("Deleted"); refetch("out"); }}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ListTree className="h-5 w-5" /> About this engine</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Hierarchy: <Badge variant="secondary">Curriculum → Class → Subject → Topic → Sub-topic → Outcome</Badge>.
            Each level supports create, edit, and delete with validation. Use search to filter loaded lists.
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function Column({
  title, icon, items, empty, disabled, activeId, onPick, addDialog, editDialog, onDelete,
}: {
  title: string;
  icon: React.ReactNode;
  items: { id: string; label: string; code?: string; raw: AnyRow }[];
  empty: string;
  disabled: boolean;
  activeId: string;
  onPick: (id: string) => void;
  addDialog: React.ReactNode;
  editDialog?: (row: AnyRow) => React.ReactNode;
  onDelete?: (row: AnyRow) => Promise<void>;
}) {
  return (
    <Card className={disabled ? "opacity-60" : ""}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">{icon}{title}</CardTitle>
        {!disabled && addDialog}
      </CardHeader>
      <CardContent className="space-y-1 max-h-[420px] overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center">{empty}</p>
        ) : (
          items.map((it) => (
            <div
              key={it.id}
              className={`w-full rounded-md px-2 py-2 text-sm border transition flex items-start gap-2 ${
                activeId === it.id ? "bg-primary/10 border-primary" : "hover:bg-muted border-transparent"
              }`}
            >
              <button onClick={() => onPick(it.id)} className="flex-1 text-left">
                <div className="flex items-start justify-between gap-2">
                  <span className="line-clamp-2">{it.label}</span>
                  {it.code && <Badge variant="outline" className="text-[10px] shrink-0">{it.code}</Badge>}
                </div>
              </button>
              {editDialog?.(it.raw)}
              {onDelete && (
                <DeleteButton label={it.label} onConfirm={() => onDelete(it.raw)} />
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function DeleteButton({ label, onConfirm }: { label: string; onConfirm: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setOpen(true)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes <strong>{label}</strong> and may fail if it has linked content.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={async (e) => {
              e.preventDefault();
              setBusy(true);
              try { await onConfirm(); setOpen(false); }
              catch (err) { toast.error((err as Error).message); }
              finally { setBusy(false); }
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  trigger, title, fields, onSubmit, initial,
}: {
  trigger: React.ReactNode;
  title: string;
  fields: Field[];
  onSubmit: (v: Record<string, unknown>) => Promise<unknown>;
  initial?: AnyRow;
}) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function openWith() {
    const seed: Record<string, string> = {};
    if (initial) {
      for (const f of fields) {
        const v = initial[f.name];
        if (v !== null && v !== undefined) seed[f.name] = String(v);
      }
    }
    setValues(seed);
    setErrors({});
    setOpen(true);
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    for (const f of fields) {
      const v = (values[f.name] ?? "").trim();
      if (f.required && !v) e[f.name] = "Required";
      if ("type" in f && f.type === "number" && v && Number.isNaN(Number(v))) e[f.name] = "Must be a number";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    try {
      const out: Record<string, unknown> = {};
      for (const f of fields) {
        const v = values[f.name];
        if (v === undefined || v === "") continue;
        if ("type" in f && f.type === "number") out[f.name] = Number(v);
        else out[f.name] = v;
      }
      await onSubmit(out);
      setOpen(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span onClick={openWith}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.name}>
                <Label>{f.label}{f.required && " *"}</Label>
                {"select" in f ? (
                  <Select value={values[f.name] ?? ""} onValueChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {f.select.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : "textarea" in f && f.textarea ? (
                  <Textarea
                    value={values[f.name] ?? ""}
                    onChange={(e) => setValues((s) => ({ ...s, [f.name]: e.target.value }))}
                  />
                ) : (
                  <Input
                    type={"type" in f && f.type === "number" ? "number" : "text"}
                    value={values[f.name] ?? ""}
                    onChange={(e) => setValues((s) => ({ ...s, [f.name]: e.target.value }))}
                  />
                )}
                {errors[f.name] && <p className="text-xs text-destructive mt-1">{errors[f.name]}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ============================================================
 * Bulk CSV import
 * ============================================================ */

const CSV_HEADERS = [
  "curriculum_code", "curriculum_name",
  "class_code", "class_name", "class_level",
  "subject_code", "subject_name",
  "topic_code", "topic_name",
  "subtopic_code", "subtopic_name",
  "outcome_code", "outcome_statement", "bloom_level",
];

const CSV_TEMPLATE =
  CSV_HEADERS.join(",") + "\n" +
  [
    "UG-NCDC,Uganda NCDC,P5,Primary 5,primary,MATH,Mathematics,FRAC,Fractions,EQUIV,Equivalent fractions,OUT1,Identify equivalent fractions,understand",
    "UG-NCDC,Uganda NCDC,P5,Primary 5,primary,MATH,Mathematics,FRAC,Fractions,,,OUT2,Compare two fractions,apply",
    "UG-NCDC,Uganda NCDC,S2,Senior 2,o_level,ENG,English,,,,,,,",
  ].join("\n") + "\n";

function parseCsv(text: string): Record<string, string>[] {
  // Minimal RFC-4180-ish parser supporting quoted fields and embedded commas.
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { cur.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (field.length || cur.length) { cur.push(field); rows.push(cur); cur = []; field = ""; }
        if (c === "\r" && text[i + 1] === "\n") i++;
      } else field += c;
    }
  }
  if (field.length || cur.length) { cur.push(field); rows.push(cur); }
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1)
    .filter((r) => r.some((v) => v.trim() !== ""))
    .map((r) => {
      const obj: Record<string, string> = {};
      headers.forEach((h, idx) => {
        const v = (r[idx] ?? "").trim();
        if (v !== "") obj[h] = v;
      });
      return obj;
    });
}

type ImportResult = {
  total: number; created: number; updated: number;
  succeeded: number; failed: number;
  results: { row: number; status: "ok" | "error"; level?: string; error?: string }[];
};

function BulkImportDialog({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const bulkImport = useServerFn(bulkImportCurriculum);

  function reset() {
    setRows([]); setFileName(""); setResult(null);
  }

  async function onFile(file: File) {
    setResult(null);
    setFileName(file.name);
    const text = await file.text();
    try {
      const parsed = parseCsv(text);
      if (parsed.length === 0) { toast.error("CSV has no data rows"); setRows([]); return; }
      setRows(parsed);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function run() {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const res = await bulkImport({ data: { rows } });
      setResult(res as ImportResult);
      if (res.failed === 0) toast.success(`Imported ${res.succeeded} rows`);
      else toast.warning(`${res.succeeded} OK · ${res.failed} failed`);
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function downloadTemplate() {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "curriculum-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadErrors() {
    if (!result) return;
    const lines = ["row,status,level,error"];
    for (const r of result.results) {
      const err = (r.error ?? "").replace(/"/g, '""');
      lines.push(`${r.row},${r.status},${r.level ?? ""},"${err}"`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "curriculum-import-report.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { reset(); setOpen(true); }}>
        <Upload className="h-4 w-4 mr-1" />Bulk import
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bulk CSV import — Curriculum hierarchy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Each row may carry any prefix of the path (Curriculum → Class → Subject → Topic → Sub-topic → Outcome).
              Existing nodes are matched by <code>code</code> and updated; missing ones are created. Required columns:
              {" "}<code>curriculum_code</code>, <code>curriculum_name</code>; when a level is included, both its code and name are required
              (class also needs <code>class_level</code>: nursery | primary | o_level | a_level | tvet | university).
              <code>bloom_level</code>: remember | understand | apply | analyze | evaluate | create.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-1" />Download template
              </Button>
              <label className="inline-flex">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
                />
                <Button size="sm" variant="outline" asChild>
                  <span><Upload className="h-4 w-4 mr-1" />Choose CSV…</span>
                </Button>
              </label>
              {fileName && <Badge variant="secondary" className="self-center">{fileName} · {rows.length} rows</Badge>}
            </div>

            {result && (
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="default">Total {result.total}</Badge>
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">OK {result.succeeded}</Badge>
                  {result.failed > 0 && <Badge variant="destructive">Failed {result.failed}</Badge>}
                  <Badge variant="outline">Created {result.created}</Badge>
                  <Badge variant="outline">Updated {result.updated}</Badge>
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={downloadErrors}>
                    <Download className="h-4 w-4 mr-1" />Download report
                  </Button>
                </div>
                <div className="max-h-64 overflow-auto rounded border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left p-1.5 w-14">Row</th>
                        <th className="text-left p-1.5 w-20">Status</th>
                        <th className="text-left p-1.5 w-24">Level</th>
                        <th className="text-left p-1.5">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((r) => (
                        <tr key={r.row} className="border-t">
                          <td className="p-1.5">{r.row}</td>
                          <td className={`p-1.5 font-medium ${r.status === "ok" ? "text-emerald-600" : "text-destructive"}`}>
                            {r.status}
                          </td>
                          <td className="p-1.5 capitalize">{r.level ?? ""}</td>
                          <td className="p-1.5">{r.error ?? "Saved"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Close</Button>
            <Button onClick={run} disabled={busy || rows.length === 0}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-1" />}
              Import {rows.length || ""} rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

